// ============================================
// OnboardingHint — inline squiggly arrow pointing at the AccountMenu (W4a)
// ============================================
//
// Renders ONLY when ALL of the following are true:
//   1. VITE_FEATURE_CLOUD_SYNC === 'true'
//   2. currentUser.value === null  (user is not logged in)
//   3. At least one CEFR level's localStorage progress map is non-empty
//      (≥1 entry in english-word-progress-{A1|A2|B1|B2|C1|C2|Everyday})
//
// Dismissible: clicking the × or pressing Escape dismisses the hint for
// the current session (module-scope boolean; never persisted to storage).
// Does NOT trap focus — the dismiss button is in the natural tab order.
//
// The squiggly arrow is an inline SVG (no external asset). The label uses
// a script-like Tailwind font styling to feel hand-drawn.

import { useState } from 'preact/hooks';
import type { ProgressMap } from '@kevvykevwin/lang-core/types';
import { currentUser } from '../state/syncSignals.js';
import { cloudSyncEnabled } from '../featureFlags.js';
import { signals, storage } from '../srs.js';
import { ENGLISH_LEVELS } from '../types.js';

// Session-scoped dismissed flag — survives component re-renders but resets
// on page reload (exactly what the spec requires: "does not reappear this session").
let dismissed = false;

// `activeMap` is the live signal value for the current level — passed in so the
// component re-renders the moment the user answers their first card (the signal
// updates synchronously, before lang-core's debounced localStorage flush). Other
// levels are read from storage (already persisted by the time they're switched away from).
function hasLocalProgress(activeMap: ProgressMap): boolean {
  if (Object.keys(activeMap).length > 0) return true;
  try {
    for (const level of ENGLISH_LEVELS) {
      // storage.getWordProgress reads + parses the english-word-progress-<level>
      // key (returns {} on missing/malformed), so we just check for ≥1 entry.
      if (Object.keys(storage.getWordProgress(level)).length > 0) return true;
    }
  } catch {
    // localStorage unavailable or parse error — treat as no progress.
  }
  return false;
}

// ── Inner component ────────────────────────────────────────────────────────
// Hooks are always called unconditionally here. Conditional guards live in
// the returned JSX and the wrapper below, satisfying react-hooks/rules-of-hooks.

function OnboardingHintInner() {
  const [isDismissed, setIsDismissed] = useState(dismissed);

  // Read the active-level progress signal so this component re-renders the
  // instant the user answers their first card (reactive entry-point reveal).
  const activeMap = signals.wordProgress.value;

  // If any gate fails, render nothing.
  if (currentUser.value !== null) return null;
  if (!hasLocalProgress(activeMap)) return null;
  if (isDismissed) return null;

  const handleDismiss = () => {
    dismissed = true;       // persist for the remainder of the session
    setIsDismissed(true);   // trigger re-render
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') handleDismiss();
  };

  return (
    <div
      className="
        absolute top-full right-0 mt-2
        hidden sm:flex items-center gap-1
        pointer-events-auto
        z-30
        max-w-[calc(100vw-1rem)]
      "
      onKeyDown={handleKeyDown}
      role="status"
      aria-live="polite"
      aria-label="Sign in to save your progress across devices"
    >
      {/* Hand-drawn squiggly arrow SVG pointing up-right toward the avatar */}
      <svg
        width="72"
        height="40"
        viewBox="0 0 72 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className="flex-shrink-0"
      >
        {/* Squiggly horizontal shaft */}
        <path
          d="M4 28 Q10 22 16 28 Q22 34 28 28 Q34 22 40 28 Q46 34 52 28 Q58 22 64 20"
          stroke="#6366f1"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
        {/* Arrowhead pointing up-right */}
        <path
          d="M64 20 L58 16 M64 20 L60 26"
          stroke="#6366f1"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {/* Script-style label */}
      <span
        className="
          text-xs text-indigo-600 font-semibold italic leading-tight
          whitespace-nowrap select-none
        "
        style={{ fontFamily: "'Georgia', serif" }}
      >
        save your progress
      </span>

      {/* Dismiss button — in natural tab order, no focus trap */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss sign-in hint"
        className="
          ml-1 w-5 h-5 flex items-center justify-center
          rounded-full text-slate-400 hover:text-slate-600
          hover:bg-slate-200 transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400
          flex-shrink-0
        "
      >
        <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" aria-hidden="true">
          <path d="M9.77 2.23a.75.75 0 0 0-1.06 0L6 4.94 3.29 2.23a.75.75 0 0 0-1.06 1.06L4.94 6 2.23 8.71a.75.75 0 0 0 1.06 1.06L6 7.06l2.71 2.71a.75.75 0 0 0 1.06-1.06L7.06 6l2.71-2.71a.75.75 0 0 0 0-1.06z" />
        </svg>
      </button>
    </div>
  );
}

// ── Public export (flag-guarded wrapper) ──────────────────────────────────
// The flag check lives here so that hooks inside OnboardingHintInner are
// always called unconditionally — satisfying react-hooks/rules-of-hooks.

export function OnboardingHint() {
  if (!cloudSyncEnabled()) return null;
  return <OnboardingHintInner />;
}
